import type { ReactNode } from 'react';
import { formatCurrency, formatQuantity } from '../../utils/formatCurrency';

export type InventoryMovementTotalsBarMode = 'selected' | 'page';

export interface InventoryMovementTotalsBarProps {
  mode: InventoryMovementTotalsBarMode;
  count: number;
  qtyChange: number;
  valueChange: number;
  qtyAddedToStock?: number;
  hasReceiptBackorderSplit?: boolean;
  hideQty?: boolean;
  qtyLabel?: string;
  valueLabel?: string;
  pageCountSuffix?: string;
  onClear?: () => void;
  children?: ReactNode;
}

const InventoryMovementTotalsBar = ({
  mode,
  count,
  qtyChange,
  valueChange,
  qtyAddedToStock = 0,
  hasReceiptBackorderSplit = false,
  hideQty = false,
  qtyLabel = 'Qty',
  valueLabel = 'Value',
  pageCountSuffix = '',
  onClear,
  children,
}: InventoryMovementTotalsBarProps) => {
  const countLabel =
    mode === 'selected'
      ? `${count} selected`
      : `Totals (${count}${pageCountSuffix})`;

  return (
    <div className="z-20 flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="font-medium text-gray-900">{countLabel}</span>
        {!hideQty && (
          <span className="text-gray-600">
            {qtyLabel}{' '}
            <span
              className={`font-medium font-mono ${
                qtyChange >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {qtyChange >= 0 ? '+' : ''}
              {formatQuantity(qtyChange)}
            </span>
            {hasReceiptBackorderSplit && (
              <span className="ml-1 text-xs font-normal text-gray-500">
                ({formatQuantity(qtyAddedToStock)} added to stock)
              </span>
            )}
          </span>
        )}
        <span className="text-gray-600">
          {valueLabel}{' '}
          <span
            className={`font-medium font-mono ${
              valueChange >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatCurrency(valueChange)}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {children}
        {mode === 'selected' && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-slate-700 underline hover:text-slate-900"
          >
            Clear selection
          </button>
        )}
      </div>
    </div>
  );
};

export default InventoryMovementTotalsBar;
